import { ApiService } from './api-service';
import { User, UserFormData } from '@/features/users/types';

export interface UsersResponse {
  results: User[];
  total: number;
  limit: number;
  offset: number;
}

export interface UserAPIResponse {
  id: string;
  username: string;
  email: string;
  full_name: string;
  role: string;
  is_active: number;
  two_factor_enabled: number;
  created_at: number;
  updated_at: number;
  last_login?: number;
}

export class UsersService {
  private static apiService = new ApiService();

  // Convert API response to frontend User type
  private static mapApiResponseToUser(apiUser: UserAPIResponse): User {
    return {
      id: apiUser.id,
      username: apiUser.username,
      email: apiUser.email,
      fullName: apiUser.full_name,
      role: apiUser.role as any,
      isActive: apiUser.is_active === 1,
      twoFactorEnabled: apiUser.two_factor_enabled === 1,
      createdAt: new Date(apiUser.created_at * 1000).toISOString(),
      lastLogin: apiUser.last_login ? new Date(apiUser.last_login * 1000).toISOString() : undefined,
    };
  }

  // Convert frontend UserFormData to API payload
  private static mapUserFormDataToApiPayload(userData: UserFormData) {
    return {
      username: userData.username,
      email: userData.email,
      full_name: userData.fullName,
      role: userData.role,
      is_active: userData.isActive ? 1 : 0,
      two_factor_enabled: userData.twoFactorEnabled ? 1 : 0,
      ...(userData.password && { password: userData.password }),
    };
  }

  // Helper method to handle API responses
  private static async handleApiResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} - ${errorText}`);
    }
    return response.json();
  }

  static async getUsers(params?: {
    role?: string;
    q?: string;
    limit?: number;
    offset?: number;
  }): Promise<UsersResponse> {
    try {
      const searchParams = new URLSearchParams();
      
      if (params?.role) searchParams.append('role', params.role);
      if (params?.q) searchParams.append('q', params.q);
      if (params?.limit) searchParams.append('limit', params.limit.toString());
      if (params?.offset) searchParams.append('offset', params.offset.toString());

      const endpoint = `/users${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
      const response = await this.apiService.get(endpoint);
      const data = await this.handleApiResponse<{
        results: UserAPIResponse[];
        total: number;
        limit: number;
        offset: number;
      }>(response);

      return {
        results: data.results.map(this.mapApiResponseToUser),
        total: data.total,
        limit: data.limit,
        offset: data.offset,
      };
    } catch (error) {
      console.error('Error fetching users:', error);
      throw new Error('Failed to fetch users');
    }
  }

  static async getUser(id: string): Promise<User> {
    try {
      const response = await this.apiService.get(`/users/${id}`);
      const data = await this.handleApiResponse<UserAPIResponse>(response);
      return this.mapApiResponseToUser(data);
    } catch (error) {
      console.error('Error fetching user:', error);
      throw new Error('Failed to fetch user');
    }
  }

  static async createUser(userData: UserFormData): Promise<User> {
    try {
      const payload = this.mapUserFormDataToApiPayload(userData);
      const response = await this.apiService.post('/users', payload);
      const data = await this.handleApiResponse<UserAPIResponse>(response);
      return this.mapApiResponseToUser(data);
    } catch (error) {
      console.error('Error creating user:', error);
      throw new Error('Failed to create user');
    }
  }

  static async updateUser(id: string, userData: Partial<UserFormData>): Promise<User> {
    try {
      const payload = this.mapUserFormDataToApiPayload(userData as UserFormData);
      const response = await this.apiService.patch(`/users/${id}`, payload);
      const data = await this.handleApiResponse<UserAPIResponse>(response);
      return this.mapApiResponseToUser(data);
    } catch (error) {
      console.error('Error updating user:', error);
      throw new Error('Failed to update user');
    }
  }

  static async deleteUser(id: string): Promise<void> {
    try {
      const response = await this.apiService.delete(`/users/${id}`);
      await this.handleApiResponse<void>(response);
    } catch (error) {
      console.error('Error deleting user:', error);
      throw new Error('Failed to delete user');
    }
  }

  static async toggleUserStatus(id: string, isActive: boolean): Promise<User> {
    try {
      const payload = {
        is_active: isActive ? 1 : 0,
      };
      const response = await this.apiService.patch(`/users/${id}`, payload);
      const data = await this.handleApiResponse<UserAPIResponse>(response);
      return this.mapApiResponseToUser(data);
    } catch (error) {
      console.error('Error toggling user status:', error);
      throw new Error('Failed to toggle user status');
    }
  }

  static async resetUserPassword(userId: string): Promise<void> {
    try {
      // This would typically generate a reset token and send email
      // For now, we'll implement a basic reset that requires admin to set new password
      const response = await this.apiService.post(`/users/${userId}/reset-password`);
      await this.handleApiResponse<void>(response);
    } catch (error) {
      console.error('Error resetting user password:', error);
      throw new Error('Failed to reset user password');
    }
  }
}
